/**
 * TemplateLibraryPage: filterable list of past layouts (PRD §12.3). Per
 * the locked data model decision, a "template" is just any existing
 * wedding's layout — no separate template entity. Filter by room and
 * guest-count range so the coordinator can find "something close to 150
 * guests in Candlelight" quickly.
 *
 * Only weddings that actually have a generated layout are shown — a
 * wedding still sitting at "no layout yet" can't meaningfully be used as
 * a template, and attempting to duplicate one would fail with a database
 * error rather than anything a coordinator could act on.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplateWeddings, useCreateWedding } from '../api/weddings';
import { useDuplicateLayout, useWeddingIdsWithLayouts } from '../api/layouts';
import { useAuth } from '../auth/AuthProvider';
import type { Room } from '../domain/types';

const ROOM_LABELS: Record<Room, string> = {
  candlelight: 'Candlelight',
  conservatory: 'Conservatory',
  tree_deck: 'Tree Deck',
};

export function TemplateLibraryPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: weddings, isLoading } = useTemplateWeddings();
  const { data: weddingIdsWithLayouts, isLoading: layoutIdsLoading } = useWeddingIdsWithLayouts();
  const createWedding = useCreateWedding();
  const duplicateLayout = useDuplicateLayout();

  const [roomFilter, setRoomFilter] = useState<Room | 'all'>('all');
  const [minGuests, setMinGuests] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  // Templates are created by "Save as Template" which always copies the layout,
  // so every template should have a layout. The has(w.id) guard is kept as a
  // safety valve in case a template record was somehow created without one.
  const weddingsWithLayouts = useMemo(() => {
    if (!weddings || !weddingIdsWithLayouts) return [];
    return weddings.filter((w) => weddingIdsWithLayouts.has(w.id));
  }, [weddings, weddingIdsWithLayouts]);

  const filtered = useMemo(() => {
    return weddingsWithLayouts.filter((w) => {
      if (roomFilter !== 'all' && w.room !== roomFilter) return false;
      if (minGuests && w.guestCount < Number(minGuests)) return false;
      if (maxGuests && w.guestCount > Number(maxGuests)) return false;
      return true;
    });
  }, [weddingsWithLayouts, roomFilter, minGuests, maxGuests]);

  async function handleDuplicate(sourceWeddingId: string) {
    if (!profile) return;
    const source = weddings?.find((w) => w.id === sourceWeddingId);
    if (!source) return;

    setDuplicateError(null);
    setDuplicatingId(sourceWeddingId);
    try {
      const newWedding = await createWedding.mutateAsync({
        coupleNames: 'New Wedding (from template)',
        eventDate: new Date().toISOString().slice(0, 10),
        room: source.room,
        guestCount: source.guestCount,
        headTableType: source.headTableType,
        headTablePartySize: source.headTablePartySize,
        layoutStyle: source.layoutStyle,
        createdBy: profile.id,
        isTemplateReference: false,  // explicitly a normal wedding, not another template
      });

      await duplicateLayout.mutateAsync({
        sourceWeddingId,
        targetWeddingId: newWedding.id,
      });

      navigate(`/weddings/${newWedding.id}`);
    } catch {
      setDuplicateError("Something went wrong duplicating that layout. Please try again, or pick a different one.");
    } finally {
      setDuplicatingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <h1 className="font-display text-2xl text-ink-700">Templates</h1>
      <p className="mt-1 font-body text-sm text-ink-400">
        Duplicate any past wedding&rsquo;s layout as a starting point for a new one.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-lg border border-ivory-300 bg-white p-4">
        <label className="block">
          <span className="block font-body text-xs text-ink-400">Room</span>
          <select
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value as Room | 'all')}
            className="mt-1 rounded border border-ivory-300 bg-white px-2 py-1.5 font-body text-sm text-ink-700"
          >
            <option value="all">All rooms</option>
            <option value="candlelight">Candlelight</option>
            <option value="conservatory">Conservatory</option>
            <option value="tree_deck">Tree Deck</option>
          </select>
        </label>

        <label className="block">
          <span className="block font-body text-xs text-ink-400">Min guests</span>
          <input
            type="number"
            value={minGuests}
            onChange={(e) => setMinGuests(e.target.value)}
            className="mt-1 w-24 rounded border border-ivory-300 bg-white px-2 py-1.5 font-body text-sm text-ink-700"
          />
        </label>

        <label className="block">
          <span className="block font-body text-xs text-ink-400">Max guests</span>
          <input
            type="number"
            value={maxGuests}
            onChange={(e) => setMaxGuests(e.target.value)}
            className="mt-1 w-24 rounded border border-ivory-300 bg-white px-2 py-1.5 font-body text-sm text-ink-700"
          />
        </label>
      </div>

      {duplicateError && (
        <p className="mt-4 rounded border border-danger/30 bg-danger/5 px-3 py-2 font-body text-sm text-danger">
          {duplicateError}
        </p>
      )}

      {(isLoading || layoutIdsLoading) && (
        <p className="mt-6 font-body text-sm text-ink-400">{'Loading\u2026'}</p>
      )}

      <div className="mt-4 divide-y divide-ivory-200 rounded-lg border border-ivory-300 bg-white">
        {filtered.map((wedding) => (
          <div key={wedding.id} className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="font-body text-sm font-medium text-ink-700">{wedding.coupleNames}</p>
              <p className="font-body text-xs text-ink-400">
                {ROOM_LABELS[wedding.room]} {'\u00b7'} {wedding.guestCount} guests {'\u00b7'}{' '}
                {wedding.layoutStyle.replace(/_/g, ' ')}
              </p>
            </div>
            <button
              onClick={() => handleDuplicate(wedding.id)}
              disabled={duplicatingId === wedding.id}
              className="rounded border border-evergreen-300 px-3 py-1.5 font-body text-sm text-evergreen-700 hover:bg-evergreen-50 disabled:opacity-50"
            >
              {duplicatingId === wedding.id ? 'Duplicating\u2026' : 'Duplicate to New Wedding'}
            </button>
          </div>
        ))}

        {!isLoading && !layoutIdsLoading && filtered.length === 0 && weddingsWithLayouts.length === 0 && (
          <p className="px-4 py-6 text-center font-body text-sm text-ink-400">
            No saved templates yet. Open any wedding in the editor and use{' '}
            &ldquo;Save as Template&rdquo; to add it here.
          </p>
        )}

        {!isLoading && !layoutIdsLoading && filtered.length === 0 && weddingsWithLayouts.length > 0 && (
          <p className="px-4 py-6 text-center font-body text-sm text-ink-400">
            No weddings match these filters. Try widening your guest count range.
          </p>
        )}
      </div>
    </div>
  );
}
